// Sistema padronizado de carrinho para Produtos Poderosos
class CarrinhoManager {
  constructor() {
    this.init()
  }

  init() {
    // Adicionar estilos CSS para feedback
    this.addFeedbackStyles()
  }

  // Adicionar produto ao carrinho com loading global
  async addToCart(variantId, quantity = 1, buttonElement = null) {
    try {
      // Mostrar loading no botão se fornecido
      if (buttonElement) {
        this.showLoading(buttonElement)
      }

      // Verificar se o produto já está no carrinho
      const cartResponse = await fetch('/cart.js')
      const cartData = await this.parseShopifyJsonResponse(cartResponse, { action: 'read-cart' })

      // Verificar se o produto já existe no carrinho
      const existingItem = cartData.items.find((item) => item.variant_id.toString() === variantId.toString())

      if (existingItem) {
        // Produto já existe no carrinho - somar a quantidade
        const newQuantity = existingItem.quantity + quantity
        
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            updates: {
              [existingItem.key]: newQuantity
            }
          })
        })

        const updatedCartData = await this.parseShopifyJsonResponse(updateResponse, {
          action: 'update-cart',
          variantId,
          quantity
        })

        // Abrir o carrinho imediatamente
        this.abrirCarrinho()

        // Atualizar contador do carrinho e conteúdo em paralelo
        // Usar Promise.allSettled para não falhar se uma das promises falhar
        const updatePromises = [
          this.updateCartCount().catch(err => {
            console.warn('Erro ao atualizar contador:', err);
          }),
          this.atualizarCarrinhoComDados(updatedCartData).catch(err => {
            console.warn('Erro ao atualizar carrinho:', err);
          })
        ]
        await Promise.allSettled(updatePromises)

        // Disparar evento personalizado para atualizar o nav
        document.dispatchEvent(new CustomEvent('cart:updated'))

        // Mostrar feedback de sucesso
        this.showAlert('Quantidade atualizada no carrinho!', 'preto')

        // Remover loading
        if (buttonElement) {
          setTimeout(() => {
            this.hideLoading(buttonElement)
          }, 1000)
        }

        return updatedCartData
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity
            }
          ]
        })
      })

      const data = await this.parseShopifyJsonResponse(response, {
        action: 'add-to-cart',
        variantId,
        quantity
      })

      // Abrir o carrinho imediatamente (antes de atualizar para melhor UX)
      this.abrirCarrinho()

      // Atualizar contador do carrinho e conteúdo em paralelo
      // Usar Promise.allSettled para não falhar se uma das promises falhar
      const updatePromises = [
        this.updateCartCount().catch(err => {
          console.warn('Erro ao atualizar contador:', err);
        }),
        this.atualizarCarrinhoComDados(data).catch(err => {
          console.warn('Erro ao atualizar carrinho:', err);
        })
      ]
      await Promise.allSettled(updatePromises)

      // Disparar evento personalizado para atualizar o nav
      document.dispatchEvent(new CustomEvent('cart:updated'))

      // Mostrar feedback de sucesso
      this.showAlert('Produto adicionado ao carrinho!', 'preto')

      // Remover loading
      if (buttonElement) {
        setTimeout(() => {
          this.hideLoading(buttonElement)
        }, 1000)
      }

      return data
    } catch (error) {
      console.error('Erro ao adicionar ao carrinho:', error)

      const mensagemErro = String(error && error.message ? error.message : '')
      const erroEstoque =
        mensagemErro.toLowerCase().includes('indispon') ||
        mensagemErro.toLowerCase().includes('sold out') ||
        mensagemErro.toLowerCase().includes('not enough') ||
        mensagemErro.toLowerCase().includes('422')

      this.showAlert(
        erroEstoque ? 'Quantidade indisponível para este tamanho.' : 'Erro ao adicionar produto ao carrinho!',
        'negativo'
      )

      // Remover loading em caso de erro
      if (buttonElement) {
        this.hideLoading(buttonElement)
      }

      throw error
    }
  }

  isVerificationGateResponse(text = '') {
    const normalized = String(text).toLowerCase()
    return (
      normalized.includes('your connection needs to be verified before you can proceed') ||
      normalized.includes('verify you are human') ||
      normalized.includes('cloudflare') ||
      normalized.includes('cf-challenge') ||
      normalized.includes('cf-browser-verification') ||
      normalized.includes('attention required')
    )
  }

  forceAddWithNavigation(variantId, quantity) {
    if (!variantId) return
    const qty = Number(quantity) > 0 ? Number(quantity) : 1
    window.location.href = `/cart/add?id=${encodeURIComponent(variantId)}&quantity=${qty}`
  }

  async parseShopifyJsonResponse(response, context = {}) {
    const contentType = (response.headers.get('content-type') || '').toLowerCase()
    const looksLikeJson =
      contentType.includes('application/json') ||
      contentType.includes('text/javascript')

    if (!response.ok) {
      const bodyText = await response.text().catch(() => '')
      if (response.status === 401 || response.status === 403 || this.isVerificationGateResponse(bodyText)) {
        this.showAlert('Validação de segurança detectada. Redirecionando para continuar a compra...', 'negativo')
        this.forceAddWithNavigation(context.variantId, context.quantity)
      }

      let shopifyMessage = ''
      try {
        const bodyJson = JSON.parse(bodyText)
        shopifyMessage = bodyJson.description || bodyJson.message || bodyJson.status || ''
      } catch (error) {
        shopifyMessage = bodyText
      }

      if (response.status === 422) {
        throw new Error(shopifyMessage || 'Quantidade indisponível para este tamanho.')
      }

      throw new Error(shopifyMessage || `Erro HTTP ${response.status}`)
    }

    if (looksLikeJson) {
      return response.json()
    }

    const bodyText = await response.text()
    if (this.isVerificationGateResponse(bodyText)) {
      this.showAlert('Validação de segurança detectada. Redirecionando para continuar a compra...', 'negativo')
      this.forceAddWithNavigation(context.variantId, context.quantity)
      throw new Error('Resposta de verificação recebida no lugar de JSON do carrinho')
    }

    try {
      return JSON.parse(bodyText)
    } catch (error) {
      throw new Error('Resposta inesperada do carrinho (não JSON)')
    }
  }

  // Atualizar contador do carrinho
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js')
      const cart = await response.json()

      // Atualizar elementos do carrinho na página
      const carrinhoButtons = document.querySelectorAll('nav .botoes .padrao')
      carrinhoButtons.forEach((button) => {
        if (button.textContent.includes('produtos')) {
          button.innerHTML = `
            <img src="/assets/icone-carrinho.svg" alt="Carrinho">
            ${cart.item_count} produtos
          `
        }
      })

      // Atualizar contador se existir elemento específico
      const cartCount = document.getElementById('cart-count')
      if (cartCount) {
        cartCount.textContent = cart.item_count
      }

      return cart
    } catch (error) {
      console.error('Erro ao atualizar contador:', error)
    }
  }

  // Sistema de alertas integrado
  showAlert(mensagem, tipo = 'positivo', duracao = 4000) {
    if (window.mostrarAlerta) {
      window.mostrarAlerta(mensagem, tipo, duracao)
    } else {
      // Fallback caso o alerta.liquid não esteja carregado
      console.log(`Alerta ${tipo}: ${mensagem}`)
    }
  }

  // Abrir popup do carrinho
  abrirCarrinho() {
    // Tentar abrir sidebar do carrinho se existir
    if (typeof openCartSidebar === 'function') {
      openCartSidebar()
      return
    }

    // Tentar abrir modal do carrinho se existir
    const cartModal = document.getElementById('cart-modal')
    if (cartModal) {
      cartModal.classList.add('mostrar')
      return
    }

    // Se não há sidebar nem modal, redirecionar para página do carrinho
    setTimeout(() => {
      window.location.href = '/cart'
    }, 1500)
  }

  // Atualizar carrinho com dados já obtidos (sem fazer nova requisição)
  async atualizarCarrinhoComDados(cartData) {
    try {
      // Disparar evento para atualizar o sidebar com os dados já disponíveis
      if (typeof atualizarCartSidebarComDados === 'function') {
        atualizarCartSidebarComDados(cartData)
      } else {
        // Fallback: usar o método padrão de atualização
        document.dispatchEvent(new CustomEvent('cart:updated'))
      }
    } catch (error) {
      // Não lançar erro, apenas logar para debug
      console.warn('Aviso ao atualizar carrinho com dados:', error)
      // Disparar evento mesmo em caso de erro para garantir atualização
      document.dispatchEvent(new CustomEvent('cart:updated'))
    }
  }

  // Mostrar loading no botão
  showLoading(buttonElement) {
    if (buttonElement) {
      buttonElement.classList.add('loading')
      buttonElement.disabled = true
    }
  }

  // Esconder loading do botão
  hideLoading(buttonElement) {
    if (buttonElement) {
      buttonElement.classList.remove('loading')
      buttonElement.disabled = false
    }
  }

  // Ir para página do produto
  goToProduct(productUrl) {
    if (productUrl && productUrl !== '#') {
      window.location.href = productUrl
    }
  }

  // Adicionar estilos CSS apenas para loading
  addFeedbackStyles() {
    if (document.getElementById('carrinho-loading-styles')) return

    const style = document.createElement('style')
    style.id = 'carrinho-loading-styles'
    style.textContent = `
      /* Loading estados */
      .loading {
        position: relative;
        pointer-events: none !important;
      }

      .loading .icone img {
        animation: spin 1s linear infinite !important;
      }

      .loading::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.1);
        border-radius: inherit;
        animation: pulse 1s infinite;
      }

      /* Animações */
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      @keyframes pulse {
        0%, 100% {
          opacity: 0.3;
        }
        50% {
          opacity: 0.6;
        }
      }
    `

    document.head.appendChild(style)
  }
}

// Instanciar o gerenciador do carrinho
const carrinho = new CarrinhoManager()

// Funções globais para compatibilidade
window.addToCart = (variantId, quantity = 1, buttonElement = null) => {
  return carrinho.addToCart(variantId, quantity, buttonElement)
}

window.goToProduct = (productUrl) => {
  return carrinho.goToProduct(productUrl)
}

// Exportar para uso em módulos
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CarrinhoManager
}
